/**
 * MarkdownText - Markdown text rendering component
 * Uses react-markdown for proper markdown parsing and rendering
 */
import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Typography, Link } from '@mui/material';

// Custom paragraph component
const MarkdownParagraph = ({ children, sx }) => (
  <Typography gutterBottom component="div" sx={sx}>
    {children}
  </Typography>
);

// Custom link component factory - needs access to onLinkClick handler
const createMarkdownLink = (onLinkClick) => {
  const MarkdownLink = ({ href, children }) => {
    // If onLinkClick is provided and href is "#", use custom handler
    if (onLinkClick && href === '#') {
      return (
        <Link
          href={href}
          fontWeight={600}
          onClick={(e) => {
            e.preventDefault();
            onLinkClick(e);
          }}
        >
          {children}
        </Link>
      );
    }

    // Otherwise use default external link behavior
    return (
      <Link href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </Link>
    );
  };
  return MarkdownLink;
};

// Custom list component
const MarkdownList = ({ children }) => (
  <ul style={{ marginTop: 0, marginBottom: '1em' }}>
    {children}
  </ul>
);

// Custom list item component
const MarkdownListItem = ({ children }) => <li>{children}</li>;

// Custom emphasis component
const MarkdownEmphasis = ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>;

// Custom strong component
const MarkdownStrong = ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>;

/**
 * MarkdownText component - Renders markdown text with MUI styling
 * @param {Object} props - Component props
 * @param {string} props.children - Markdown text content to render
 * @param {Function} props.onLinkClick - Optional click handler for links with href="#"
 * @param {Object} props.sx - Optional sx prop for paragraph styling
 * @returns {React.ReactElement} Rendered markdown
 */
const MarkdownText = ({ children, onLinkClick, sx }) => {
  const markdownComponents = {
    p: (props) => <MarkdownParagraph {...props} sx={sx} />,
    a: createMarkdownLink(onLinkClick),
    ul: MarkdownList,
    li: MarkdownListItem,
    em: MarkdownEmphasis,
    strong: MarkdownStrong,
  };

  return (
    <ReactMarkdown components={markdownComponents}>
      {children}
    </ReactMarkdown>
  );
};

MarkdownText.displayName = 'MarkdownText';

export default memo(MarkdownText);
